import { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Copy,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import AdminLayout from "../components/layout/AdminLayout";
import { axiosInstance } from "../lib/axios";
import {
  useAdminStore,
  type CostConfig,
  type ResourceValueDoc,
} from "../store/useAdminStore";
import CostConfigDrawer, {
  PROJECT_TYPES,
  TIERS,
  emptyConfig,
  type Tier,
  type AccountOption,
} from "../components/admin/CostConfigDrawer";
import { useErpEmployeesStore } from "../store/useErpEmployeesStore";
import { useFinanceStore } from "../store/useFinanceStore";
import { useProjectTypeStore } from "../store/useProjectTypeStore";
import toast from "react-hot-toast";
import { Zap, Save } from "lucide-react";

const tierStyle = (t: string) => {
  if (t === "Premium")
    return "bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 ring-amber-300";
  if (t === "Standard")
    return "bg-blue-100 text-blue-700 ring-blue-200";
  return "bg-slate-100 text-slate-700 ring-slate-200";
};

const fmtResource = (r: ResourceValueDoc) => {
  if (!r || !r.amount) return "—";
  if (r.type === "hourly") return `$${r.amount}/hr`;
  if (r.type === "percentage") return `${r.amount}%`;
  return `$${r.amount}`;
};

const AdminCostManagementPage = () => {
  const {
    costConfigs,
    fetchCostConfigs,
    createCostConfig,
    updateCostConfig,
    deleteCostConfig,
    duplicateCostConfig,
  } = useAdminStore();

  const { employees: erpEmployees, fetchEmployees } = useErpEmployeesStore();
  const { settings, fetchSettings, updateSettings, isSaving } = useFinanceStore();
  const { types: projectTypeConfigs, fetchTypes } = useProjectTypeStore();
  const projectTypeNames = useMemo(
    () => (projectTypeConfigs.length ? projectTypeConfigs.map((t) => t.name) : PROJECT_TYPES),
    [projectTypeConfigs]
  );
  const [cpc, setCpc] = useState("");
  const [avgCpw, setAvgCpw] = useState("");
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [accountFilter, setAccountFilter] = useState("");
  const [sortBy, setSortBy] = useState("updatedAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<Partial<CostConfig> | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axiosInstance
      .get("/upwork-accounts")
      .then((res) => setAccounts(res.data))
      .catch(() => setAccounts([]));
    fetchEmployees();
    fetchSettings();
    fetchTypes();
  }, [fetchEmployees, fetchSettings, fetchTypes]);

  useEffect(() => {
    if (settings) {
      setCpc(String(settings.defaultCostPerConnect ?? ""));
      setAvgCpw(String(settings.defaultAvgConnectsPerWin ?? ""));
    }
  }, [settings]);

  const saveConnectCost = async () => {
    await updateSettings({
      defaultCostPerConnect: Number(cpc) || 0,
      defaultAvgConnectsPerWin: Number(avgCpw) || 0,
    });
    toast.success("Connect cost saved");
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      const params: any = { page, limit: 25, sortBy, sortDir };
      if (search) params.search = search;
      if (typeFilter) params.projectType = typeFilter;
      if (tierFilter) params.clientTier = tierFilter;
      if (accountFilter) params.upworkAccount = accountFilter;
      fetchCostConfigs(params);
    }, 250);
    return () => clearTimeout(handler);
  }, [
    search,
    typeFilter,
    tierFilter,
    accountFilter,
    sortBy,
    sortDir,
    page,
    fetchCostConfigs,
  ]);

  const items = costConfigs?.items || [];
  const pagination = costConfigs?.pagination;

  const toggleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
  };

  const openCreate = () => {
    setEditing(emptyConfig());
    setDrawerOpen(true);
  };

  const openEdit = (cfg: CostConfig) => {
    setEditing({
      ...cfg,
      upworkAccount: cfg.upworkAccount,
    });
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    const payload = {
      ...editing,
      upworkAccount:
        typeof editing.upworkAccount === "object" && editing.upworkAccount
          ? editing.upworkAccount._id
          : editing.upworkAccount || null,
    };
    const result = editing._id
      ? await updateCostConfig(editing._id, payload as Partial<CostConfig>)
      : await createCostConfig(payload as Partial<CostConfig>);
    setSaving(false);
    if (result) {
      setDrawerOpen(false);
      setEditing(null);
    }
  };

  const handleDelete = async (cfg: CostConfig) => {
    if (!confirm(`Delete cost config for ${cfg.projectType} (${cfg.clientTier})?`))
      return;
    await deleteCostConfig(cfg._id);
  };

  const handleDuplicate = async (cfg: CostConfig) => {

    const nextTier: Tier =
      cfg.clientTier === "Basic"
        ? "Standard"
        : cfg.clientTier === "Standard"
        ? "Premium"
        : "Basic";
    await duplicateCostConfig(cfg._id, { clientTier: nextTier });
  };

  const filterActive =
    !!search || !!typeFilter || !!tierFilter || !!accountFilter;

  return (
    <AdminLayout>
      <div className="p-8 space-y-6">

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-[26px] font-bold text-slate-900 tracking-tight">
              Resource Cost Management
            </h1>
            <p className="text-[13px] text-slate-500 mt-1">
              Tier-based costing per Project Type × Client Tier × Freelance
              Account. Used by BD profitability previews & finance reporting.
            </p>
          </div>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0058c3] hover:opacity-90 text-white rounded-lg font-semibold text-[13px] transition shadow-sm"
          >
            <Plus size={16} />
            New Cost Config
          </button>
        </div>

        {/* Connect cost — drives connects spend & BD recovery backlog */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] p-4 flex flex-wrap items-end gap-4">
          <div className="flex items-center gap-2 mr-2">
            <div className="w-9 h-9 rounded-lg bg-amber-100 grid place-items-center text-amber-600 shrink-0">
              <Zap size={18} />
            </div>
            <div>
              <h2 className="text-[14px] font-bold text-slate-900">Connect Cost</h2>
              <p className="text-[11px] text-slate-500">Used for connects spend & BD recovery backlog.</p>
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">Cost per Connect ($)</label>
            <input
              type="number"
              step="0.001"
              value={cpc}
              onChange={(e) => setCpc(e.target.value)}
              className="w-36 px-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0.165"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">Avg Connects / Win</label>
            <input
              type="number"
              value={avgCpw}
              onChange={(e) => setAvgCpw(e.target.value)}
              className="w-36 px-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0"
            />
          </div>
          <button
            onClick={saveConnectCost}
            disabled={isSaving}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0058c3] hover:opacity-90 text-white rounded-md font-semibold text-[13px] transition disabled:opacity-50"
          >
            <Save size={15} />
            {isSaving ? "Saving…" : "Save"}
          </button>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] p-4 flex flex-wrap items-center gap-3">
          <div className="flex-1 relative min-w-[240px]">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search by type, tier or notes..."
              className="w-full pl-10 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPage(1);
            }}
            className="bg-slate-50 border border-slate-200 rounded-lg text-[13px] px-3 py-2.5 text-slate-600"
          >
            <option value="">Type: All</option>
            {projectTypeNames.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <select
            value={tierFilter}
            onChange={(e) => {
              setTierFilter(e.target.value);
              setPage(1);
            }}
            className="bg-slate-50 border border-slate-200 rounded-lg text-[13px] px-3 py-2.5 text-slate-600"
          >
            <option value="">Tier: All</option>
            {TIERS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <select
            value={accountFilter}
            onChange={(e) => {
              setAccountFilter(e.target.value);
              setPage(1);
            }}
            className="bg-slate-50 border border-slate-200 rounded-lg text-[13px] px-3 py-2.5 text-slate-600"
          >
            <option value="">Account: All</option>
            <option value="null">Global (all accounts)</option>
            {accounts.map((a) => (
              <option key={a._id} value={a._id}>
                {a.name}
              </option>
            ))}
          </select>
          {filterActive && (
            <button
              onClick={() => {
                setSearch("");
                setTypeFilter("");
                setTierFilter("");
                setAccountFilter("");
                setPage(1);
              }}
              className="text-[12px] text-blue-600 hover:text-blue-800 font-semibold"
            >
              Clear filters
            </button>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200">
                  <SortableTh
                    label="Project Type"
                    field="projectType"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onToggle={toggleSort}
                  />
                  <SortableTh
                    label="Tier"
                    field="clientTier"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onToggle={toggleSort}
                  />
                  <Th>Account</Th>
                  <Th align="right">Fixed</Th>
                  <Th align="right">Acct. Holder Comm.</Th>
                  <Th align="right">Upwork Fee</Th>
                  <Th align="right">Target Margin</Th>
                  <Th align="right">Other</Th>
                  <SortableTh
                    label="Updated"
                    field="updatedAt"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onToggle={toggleSort}
                  />
                  <Th align="center">Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {!costConfigs ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-6 py-14 text-center text-slate-400 text-[13px]"
                    >
                      Loading cost configurations...
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-6 py-14 text-center text-slate-400 text-[13px]"
                    >
                      No cost configurations yet. Click{" "}
                      <strong className="text-slate-700">
                        New Cost Config
                      </strong>{" "}
                      to add one.
                    </td>
                  </tr>
                ) : (
                  items.map((cfg) => (
                    <tr
                      key={cfg._id}
                      className="hover:bg-slate-50/50 transition"
                    >
                      <td className="px-4 py-4">
                        <span className="text-[13px] font-bold text-[#0070f3]">
                          {cfg.projectType}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold ring-1 ${tierStyle(
                            cfg.clientTier
                          )}`}
                        >
                          {cfg.clientTier}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {cfg.upworkAccount ? (
                          <span className="text-[12px] text-slate-700 font-semibold px-2 py-0.5 bg-slate-100 rounded">
                            {cfg.upworkAccount.name}
                          </span>
                        ) : (
                          <span className="text-[11px] text-slate-500 italic">
                            Global
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-right text-[13px] text-slate-900 font-mono">
                        {fmtResource(cfg.fixedCost)}
                      </td>
                      <td className="px-4 py-4 text-right text-[13px] font-semibold text-slate-700">
                        {cfg.commission}%
                      </td>
                      <td className="px-4 py-4 text-right text-[13px] font-bold text-[#006a61]">
                        {cfg.upworkFee}%
                      </td>
                      <td className="px-4 py-4 text-right text-[13px] font-bold text-emerald-600">
                        {cfg.profitMarginPct ? `${cfg.profitMarginPct}%` : "—"}
                      </td>
                      <td className="px-4 py-4 text-right text-[12px] text-slate-500 font-mono">
                        {cfg.otherFees?.amount
                          ? `${cfg.otherFees.amount}${
                              cfg.otherFees.type === "percentage" ? "%" : "$"
                            }`
                          : "—"}
                      </td>
                      <td className="px-4 py-4 text-[11px] text-slate-500">
                        {new Date(cfg.updatedAt).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex justify-center gap-1">
                          <button
                            onClick={() => openEdit(cfg)}
                            className="p-1.5 rounded hover:bg-blue-50 text-slate-500 hover:text-blue-600"
                            title="Edit"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => handleDuplicate(cfg)}
                            className="p-1.5 rounded hover:bg-violet-50 text-slate-500 hover:text-violet-600"
                            title="Duplicate"
                          >
                            <Copy size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(cfg)}
                            className="p-1.5 rounded hover:bg-rose-50 text-slate-500 hover:text-rose-600"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination && pagination.total > 0 && (
            <div className="bg-slate-50/50 border-t border-slate-200 px-5 py-3 flex items-center justify-between text-[12px] text-slate-600">
              <span>
                Showing{" "}
                <strong>
                  {(pagination.page - 1) * pagination.limit + 1}-
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.total
                  )}
                </strong>{" "}
                of <strong>{pagination.total}</strong>
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={pagination.page <= 1}
                  className="p-1.5 rounded hover:bg-white border border-transparent hover:border-slate-200 text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={16} />
                </button>
                {Array.from(
                  { length: Math.min(5, pagination.pages) },
                  (_, i) => i + 1
                ).map((n) => (
                  <button
                    key={n}
                    onClick={() => setPage(n)}
                    className={`px-3 py-1 rounded text-[12px] ${
                      n === pagination.page
                        ? "bg-white border border-slate-200 font-bold text-[#0058c3] shadow-sm"
                        : "hover:bg-white border border-transparent hover:border-slate-200"
                    }`}
                  >
                    {n}
                  </button>
                ))}
                <button
                  onClick={() => setPage(Math.min(pagination.pages, page + 1))}
                  disabled={pagination.page >= pagination.pages}
                  className="p-1.5 rounded hover:bg-white border border-transparent hover:border-slate-200 text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {drawerOpen && editing && (
        <CostConfigDrawer
          value={editing}
          accounts={accounts}
          employees={erpEmployees}
          saving={saving}
          projectTypes={projectTypeNames}
          typeConfigs={projectTypeConfigs}
          onChange={setEditing}
          onSave={handleSave}
          onClose={() => {
            setDrawerOpen(false);
            setEditing(null);
          }}
        />
      )}
    </AdminLayout>
  );
};

const Th = ({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "center" | "right";
}) => (
  <th
    className={`px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-${align}`}
  >
    {children}
  </th>
);

const SortableTh = ({
  label,
  field,
  sortBy,
  sortDir,
  onToggle,
}: {
  label: string;
  field: string;
  sortBy: string;
  sortDir: "asc" | "desc";
  onToggle: (f: string) => void;
}) => {
  const active = sortBy === field;
  return (
    <th
      onClick={() => onToggle(field)}
      className={`px-4 py-3 text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap text-left cursor-pointer select-none ${
        active ? "text-blue-700" : "text-slate-500 hover:text-slate-700"
      }`}
    >
      {label}
      {active && (
        <span className="ml-1 inline-block">
          {sortDir === "asc" ? "â–²" : "â–¼"}
        </span>
      )}
    </th>
  );
};

export default AdminCostManagementPage;
